import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ICurrencyPrice, IProduct } from '../../models/products.models';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { FetchAllCurrencies } from 'src/app/features/customers/store/customers.actions';
import { filter, Observable, of, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
interface AddedCurrencyControl {
  data: ICurrency;
  controls: {
    currency: FormControl; // FormControl for the currency select
    retail_price: FormGroup;
    cost: FormGroup;
  };
  controlName: string; // Name of the control in the form
}
@Component({
  selector: 'app-details-dialog-edits-pricing',
  templateUrl: './details-dialog-edits-pricing.component.html',
  styleUrls: ['./details-dialog-edits-pricing.component.scss']
})
export class DetailsDialogEditsPricingComponent implements OnInit {

  form!: FormGroup
  currencies$: Observable<ICurrency[]> = of([]);
  default_currency!: ICurrency | null | undefined;
  currencies: ICurrency[] = [];
  availableCurrencies: ICurrency[] = [];
  addedCurrencies: AddedCurrencyControl[] = [];
  showCurrencyDropdown = false;
  currenciesDropdown = {
    top: 0,
    left: 0,
    minWidth: 0
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<DetailsDialogEditsPricingComponent>,
    private fb: FormBuilder,
    private translate: TranslateService,
    private store: Store<AppState>
  ) { }

  ngOnInit(): void {
    // Initialize form with default currency first
    const defaultCurrency = this.data?.default_currency;
    this.form = this.fb.group({
      sales_price: [this.data?.sales_price ?? ''],
      cost: [this.data?.cost ?? ''],
      default_currency: [{ value: defaultCurrency || null, disabled: true }],
    });
    
    // Then subscribe to currencies
    this.store.select((state) => state.customers.allCurrencies).pipe(
      filter((currencies) => Array.isArray(currencies) && currencies.length > 0),
      take(1)
    ).subscribe((currencies) => {
      this.handleCurrencies(currencies);
    });
  }

  private handleCurrencies(currencies: ICurrency[]): void {
    this.addedCurrencies = [];
    this.currencies = currencies.filter(currency => !currency.is_default);

    this.default_currency = currencies.find(currency => currency.is_default);
    
    // Update the form with the default currency
    if (this.default_currency && this.form) {
      this.form.patchValue({
        default_currency: this.default_currency
      });
    }

    this.availableCurrencies = this.currencies.filter(currency =>
      !this.data.currencyPrices?.some((price: ICurrencyPrice) => price.currency_id === currency.id)
    );

    this.currencies.forEach((currency: ICurrency) => {
      const existedRetailPrice = this.data.currencyPrices?.find(
        (currencyPrice: ICurrencyPrice) =>
          currencyPrice.currency_id === currency.id && currencyPrice.type === 'retail_price'
      );
      const existedCost = this.data.currencyPrices?.find(
        (currencyPrice: ICurrencyPrice) =>
          currencyPrice.currency_id === currency.id && currencyPrice.type === 'cost'
      );

      if (existedRetailPrice || existedCost) {
        this.addCurrency(currency, existedRetailPrice?.amount || null, existedCost?.amount || null);
      }
    });
  }


  sanitize(event: any) {
    if (event.target.value < 0) {
      event.target.value = 0;
    }
  }

  preventNegativeInput(event: KeyboardEvent) {
    if (event.key === '-' || event.key === 'Subtract') {
      event.preventDefault();
    }
  }

  save(): boolean | void {
    if(this.form.invalid){
      this.form.markAllAsTouched()
      return false
    }
    this.dialogRef.close({
      ...this.form.value,
      currency_prices: this.addedCurrencies.flatMap(c => [
        c.controls.retail_price.value,
        c.controls.cost.value
      ]),
      addedCurrencies: this.addedCurrencies
    })
  }

  cancel(){
    this.dialogRef.close()
  }

  // Recompute available currencies based on current selections (exclude default by design in this.currencies)
  private recomputeAvailableCurrencies(): void {
    const usedIds = new Set(this.addedCurrencies.map(c => c.data.id));
    this.availableCurrencies = this.currencies.filter(c => !usedIds.has(c.id));
  }

  // Get available currencies for a specific select (includes the currently selected currency)
  getAvailableCurrenciesForSelect(index: number): ICurrency[] {
    const currentCurrency = this.addedCurrencies[index]?.data;
    const usedIds = new Set(
      this.addedCurrencies
        .map((c, i) => i !== index ? c.data.id : null)
        .filter(id => id !== null)
    );
    return this.currencies.filter(c => !usedIds.has(c.id) || c.id === currentCurrency?.id);
  }

  onCurrencyChange(index: number, currencyId: number): void {
    const current = this.addedCurrencies?.[index];
    if (!current || current.data.id === currencyId) {
      return;
    }
    const newCurrency = this.currencies.find(c => c.id === currencyId);
    if (!newCurrency) {
      return;
    }
    // Update the row's currency data
    this.addedCurrencies[index].data = newCurrency;
    // Update the currency form control
    this.addedCurrencies[index].controls.currency.patchValue(newCurrency.id);
    // Update controls metadata
    const retailGroup = this.addedCurrencies[index].controls.retail_price;
    const costGroup = this.addedCurrencies[index].controls.cost;
    retailGroup.patchValue({
      currency_id: newCurrency.id,
      name: newCurrency.name,
      symbol: newCurrency.symbol
    });
    costGroup.patchValue({
      currency_id: newCurrency.id,
      name: newCurrency.name,
      symbol: newCurrency.symbol
    });
    // Keep available list in sync
    this.recomputeAvailableCurrencies();
  }

  removeCurrency(index: number): void {
    const item = this.addedCurrencies?.[index];
    if (item) {
      // Remove the form control from the form
      if (item.controlName) {
        this.form.removeControl(item.controlName);
      }
      this.addedCurrencies.splice(index, 1);
      // Recompute available currencies
      this.recomputeAvailableCurrencies();
    }
  }

  addCurrency(currency: ICurrency | undefined, retail_amount: number | null = null, cost_amount: number | null = null): void {
    // Ensure form is initialized
    if (!this.form) {
      console.error('Form is not initialized yet');
      return;
    }
    
    // If no currency provided, use the first available one
    if (!currency && this.availableCurrencies.length > 0) {
      currency = this.availableCurrencies[0];
    }
    if (!currency) {
      return;
    }
    
    const index = this.addedCurrencies.length;
    const createControls = (currency: ICurrency, retail_amount: number | null = null, cost_amount: number | null = null) => ({
      currency: this.fb.control(currency.id, Validators.required), // FormControl for the select
      retail_price: this.fb.group({
        currency_id: [currency.id, Validators.required],
        type: ['retail_price'],
        amount: [retail_amount],
        name: [currency.name],
        symbol: [currency.symbol]
      }),
      cost: this.fb.group({
        currency_id: [currency.id, Validators.required],
        type: ['cost'],
        amount: [cost_amount],
        name: [currency.name],
        symbol: [currency.symbol]
      })
    });

    const controls = createControls(currency, retail_amount, cost_amount);
    
    // Add the currency control to the main form
    this.form.addControl(`currency_${index}`, controls.currency);
    
    controls.currency.valueChanges.subscribe((currencyId: number | null) => {
      if (currencyId !== null) {
        this.onCurrencyChange(index, currencyId);
      }
    });

    const controlName = `currency_${index}`;
    this.addedCurrencies.push({
      data: currency,
      controls: {
        currency: controls.currency,
        retail_price: controls.retail_price,
        cost: controls.cost
      },
      controlName: controlName
    });
    
    this.recomputeAvailableCurrencies();
    this.showCurrencyDropdown = false;
  }

  openCurrenciesDropdown(event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();

    this.currenciesDropdown = {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      minWidth: rect.width
    };

    this.showCurrencyDropdown = true;
  }
}
